import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ShoppingBag, Tag, Grid3x3, BookOpen, GitCompare } from 'lucide-react';
import SearchBar from './SearchBar';
import LanguageSelector from './LanguageSelector';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../config/translations';

function Header() {
  const { language, tenant } = useLanguage();
  const t = (key) => getTranslation(language, key);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      <div className="container-custom">
        {/* Top bar */}
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <ShoppingBag className="w-8 h-8 text-primary-600" />
            <span className="text-xl font-bold text-gray-900">{tenant.name}</span>
          </Link>

          {/* Desktop Search */}
          <div className="hidden md:flex flex-1 max-w-2xl mx-8">
            <SearchBar />
          </div>

          {/* Desktop Navigation - Accessibility: added aria-label */}
          <nav className="hidden md:flex items-center space-x-6" aria-label="Main navigation">
            <Link
              to="/brands"
              className="flex items-center space-x-1 text-gray-700 hover:text-primary-600 transition-colors"
              aria-current={location.pathname === '/brands' ? 'page' : undefined}
            >
              <Tag className="w-4 h-4" aria-hidden="true" />
              <span>{t('brands')}</span>
            </Link>
            <Link
              to="/categories"
              className="flex items-center space-x-1 text-gray-700 hover:text-primary-600 transition-colors"
              aria-current={location.pathname === '/categories' ? 'page' : undefined}
            >
              <Grid3x3 className="w-4 h-4" aria-hidden="true" />
              <span>{t('categories')}</span>
            </Link>
            <Link
              to="/guides"
              className="flex items-center space-x-1 text-gray-700 hover:text-primary-600 transition-colors"
              aria-current={location.pathname.startsWith('/guides') ? 'page' : undefined}
            >
              <BookOpen className="w-4 h-4" aria-hidden="true" />
              <span>{t('guides') || 'Guides'}</span>
            </Link>
            <Link
              to="/compare"
              className="flex items-center space-x-1 text-gray-700 hover:text-primary-600 transition-colors"
              aria-current={location.pathname.startsWith('/compare') ? 'page' : undefined}
            >
              <GitCompare className="w-4 h-4" aria-hidden="true" />
              <span>{t('compare') || 'Compare'}</span>
            </Link>
            <LanguageSelector />
          </nav>

          {/* Mobile menu button - Accessibility: added aria-label, aria-expanded, aria-controls; Mobile: increased touch target */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-3 rounded-lg hover:bg-gray-100 min-w-[44px] min-h-[44px] flex items-center justify-center"
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-navigation"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Mobile Search */}
        <div className="md:hidden pb-4">
          <SearchBar />
        </div>
      </div>

      {/* Mobile Navigation - Accessibility: added id and aria-label */}
      {isMobileMenuOpen && (
        <div id="mobile-navigation" className="md:hidden border-t border-gray-200 bg-white animate-fade-in">
          <nav className="container-custom py-4 space-y-2" aria-label="Mobile navigation">
            <Link
              to="/brands"
              className="flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-gray-50"
              onClick={() => setIsMobileMenuOpen(false)}
              aria-current={location.pathname === '/brands' ? 'page' : undefined}
            >
              <Tag className="w-5 h-5 text-gray-600" aria-hidden="true" />
              <span>{t('brands')}</span>
            </Link>
            <Link
              to="/categories"
              className="flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-gray-50"
              onClick={() => setIsMobileMenuOpen(false)}
              aria-current={location.pathname === '/categories' ? 'page' : undefined}
            >
              <Grid3x3 className="w-5 h-5 text-gray-600" aria-hidden="true" />
              <span>{t('categories')}</span>
            </Link>
            <Link
              to="/guides"
              className="flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-gray-50"
              onClick={() => setIsMobileMenuOpen(false)}
              aria-current={location.pathname.startsWith('/guides') ? 'page' : undefined}
            >
              <BookOpen className="w-5 h-5 text-gray-600" aria-hidden="true" />
              <span>{t('guides') || 'Guides'}</span>
            </Link>
            <Link
              to="/compare"
              className="flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-gray-50"
              onClick={() => setIsMobileMenuOpen(false)}
              aria-current={location.pathname.startsWith('/compare') ? 'page' : undefined}
            >
              <GitCompare className="w-5 h-5 text-gray-600" aria-hidden="true" />
              <span>{t('compare') || 'Compare'}</span>
            </Link>
            <div className="px-4 py-2">
              <LanguageSelector />
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}

export default Header;
